import { Immutable } from 'nuclear-js';
import CommonValues from 'resources/common-values';
import CommonStrings from 'resources/common-strings';
import DataUtils from 'data/utils';
import TermUtils from 'utils/terms';

/**
 * Determines the lexile range for the given test event.
 *
 * @param testEvent The test event from which the 'lexileScoreMin' and 'lexileScoreMax' will be determined
 * @returns {* string} holding the lexile range in the form <Lower>L-<Higher>L
 *          for August 2018, a new lexile formula is introduced that produces a beginning reader range in the
 *          format: BR<Lower>L - BR<Higher>L
 * i.e. 123L - 654L or BR for Beginning Readers (or the new BR range for newer lexile values.  If no lexile range
 * can be determined the text 'No value reported' shall be returned.
 */
export function getLexileRange(testEvent) {
    let lexileRange = CommonStrings.noValueReported;

    if (testEvent) {
        const rangeMin = testEvent.get('lexileScoreMin');
        const rangeMax = testEvent.get('lexileScoreMax');

        if (rangeMin === rangeMax && rangeMax === 'BR') {
            lexileRange = rangeMin;
        } else if (rangeMin && rangeMax) {
            if (rangeMin.includes('L')) {
                lexileRange = rangeMin + ' - ' + rangeMax;
            } else {
                lexileRange = rangeMin + 'L - ' + rangeMax + 'L';
            }
        }
    }
    return lexileRange;
}

/**
 * Determines the lexile range for the given test event.
 *
 * @param testEvent The test event from which the 'quantileScoreRange' will be determined
 * @returns {* string} holding the quantile range in the form <Lower>Q - <Higher>Q
 * i.e. 1149Q - 1299Q
 */
export function getQuantileRange(testEvent) {
    let quantileRange = CommonStrings.noValueReported;

    if (testEvent && testEvent.get('quantileScoreRange')) {
        quantileRange = testEvent.get('quantileScoreRange');
    }
    return quantileRange;
}

/**
 * Gets a test event which is a growth event for the supplied termNumber and subject if it exists within
 * the supplied list of test events.
 * @param studentTestEvents The list of test events to look within
 * @param searchTermNumber The term number of the test event
 * @param subjectBid The subject of the test event
 * @returns {Object} The test event meeting the supplied criteria or undefined if nothing can be found
 */
export function getValidGrowthTestEvent(studentTestEvents, searchTermNumber, subjectBid) {
    return studentTestEvents
        ? studentTestEvents.find(
              (te) =>
                  te.get('termNumber') === searchTermNumber &&
                  te.get('growthEventYn') === true &&
                  te.get('subject') === subjectBid
          )
        : undefined;
}

/**
 * Gets a test event which is a growth event for the supplied termNumber and subject if it exists within
 * the supplied list of test events.
 * @param studentTestEvents The list of test events to look within
 * @param searchTermNumber The term number of the test event
 * @param msid The msid (aka course ID) of the test event
 * @returns {Object} The test event meeting the supplied criteria or undefined if nothing can be found
 */
export function getValidGrowthTestEventByMsid(studentTestEvents, searchTermNumber, msid) {
    return studentTestEvents
        ? studentTestEvents.find(
              (te) =>
                  te.get('termNumber') === searchTermNumber &&
                  te.get('growthEventYn') === true &&
                  te.get('measurementScaleID') === msid
          )
        : undefined;
}

/**
 * Given a term number (signifying the term number selected within the student profile application) this will return
 * a list of 'from' term numbers for a drop down list within the comparisons module.
 * @param toTermNumber The term number we are comparing to
 * @returns {Array} Containing a list of term numbers we are comparing from
 */
export function getGrowthComparisonFromTermNumbers(toTermNumber) {
    let fromTermNumbers = [];
    if (toTermNumber) {
        //Calculate other term numbers
        // WINTER_TERM = 1 - SPRING_TERM = 2 - FALL_TERM = 4;
        const currentlySelectedTermSeasonNumber = toTermNumber % 10;
        const previousYear = -100;

        if (currentlySelectedTermSeasonNumber === CommonValues.WINTER_TERM) {
            //Add Fall AY2 - Winter AY2
            const previousFallFromWinter = previousYear + 3;
            fromTermNumbers.push(toTermNumber + previousFallFromWinter);
        } else if (currentlySelectedTermSeasonNumber === CommonValues.SPRING_TERM) {
            //Add Fall AY2 - Spring AY2
            const previousFallFromSpring = previousYear + 2;
            fromTermNumbers.push(toTermNumber + previousFallFromSpring);

            //Add Winter AY2 - Spring AY2
            const previousWinterFromSpring = -1;
            fromTermNumbers.push(toTermNumber + previousWinterFromSpring);
        }

        fromTermNumbers.push(toTermNumber + previousYear);
    }

    return fromTermNumbers;
}

export function getMeanDescription(percentage, isExpanded) {
    const expandedMarker = isExpanded ? '-' : '';

    const above = expandedMarker + ' Above Mean ' + expandedMarker;
    const at = expandedMarker + ' At Mean ' + expandedMarker;
    const below = expandedMarker + ' Below Mean ' + expandedMarker;

    return percentage < 41 ? below : percentage > 60 ? above : at;
}

/**
 * Returns an object containing the valid comparison periods, or null if there are none.  This is meant to be used to
 * back a dropdown list from which a user can select a period and be shown data about the selected period.
 * @param courseSpecificBid The subjectBid of the subject a user is looking at
 * @param selectedTermNumber The term number selected by the user
 * @param studentTestEvents A list of test events for the student being looked at
 * @param comparisonGrowthPeriods A list of growth comparison periods supplied via the norms service for the selected
 * students test events.
 * @returns {Map} A map containing the data required for the dropdown list.  Each item within the map has the periods
 * display name, and data associated with that period.
 */
export function getComparisonDropdownPeriods(
    courseSpecificBid, // Can be a subject bid as per spv2 or a measurement scale if course specific
    selectedTermNumber,
    studentTestEvents,
    comparisonGrowthPeriods,
    terms = null
) {
    // See if the supplied courseSpecificBid is a spv2 Subject or is course specific
    const isCourseSpecific = CommonValues.isCourseSpecificMSID(courseSpecificBid);

    if (comparisonGrowthPeriods && terms) {
        if (studentTestEvents && selectedTermNumber !== CommonValues.mostRecentTermNumber) {
            const conditionalPushItem = (toTestEvent, fromTermNumber, payload) => {
                const toTermNumber = toTestEvent.get('termNumber');

                let fromTestEvent;
                if (isCourseSpecific) {
                    fromTestEvent = getValidGrowthTestEventByMsid(
                        studentTestEvents,
                        fromTermNumber,
                        toTestEvent.get('measurementScaleID')
                    );
                } else {
                    fromTestEvent = getValidGrowthTestEvent(studentTestEvents, fromTermNumber, courseSpecificBid);
                }

                if (fromTestEvent) {
                    const fromTerm = terms.find((t) => t.get('termNumber') === fromTestEvent.get('termNumber'), null);
                    const toTerm = terms.find((t) => t.get('termNumber') === toTermNumber, null);
                    if (fromTerm && toTerm) {
                        const normGrowth = comparisonGrowthPeriods.find(
                            (period) =>
                                period.get('grade') === toTerm.get('grade') &&
                                period.get('startingRit') === fromTestEvent.get('score') &&
                                period.get('endingRit') === toTestEvent.get('score') &&
                                period.get('startingSem') === fromTestEvent.get('standardError') &&
                                period.get('endingSem') === toTestEvent.get('standardError') &&
                                period.get('startingSeason') === fromTestEvent.get('termNumber') % 10 &&
                                period.get('endingSeason') === toTestEvent.get('termNumber') % 10 &&
                                period.get('subject') === courseSpecificBid
                        );
                        if (normGrowth) {
                            // Also Make sure the value returned via the norms service contains
                            // valid values for the expected fields - as it can return null values - which are no use
                            if (
                                normGrowth.get('studentGrowthPercentile') &&
                                normGrowth.get('studentConditionalGain') &&
                                normGrowth.get('studentConditionalGrowthIndex')
                            ) {
                                payload.push({
                                    displayRange:
                                        TermUtils.textFromTermNumber(fromTermNumber, true) +
                                        ' - ' +
                                        TermUtils.textFromTermNumber(toTermNumber, true),
                                    growthPercentile: normGrowth.get('studentGrowthPercentile'),
                                    predictedGrowth: normGrowth.get('studentConditionalGain'),
                                    observedGrowth: toTestEvent.get('score') - fromTestEvent.get('score'),
                                    conditionalGrowthIndex: normGrowth.get('studentConditionalGrowthIndex'),
                                });
                            }
                        }
                    }
                }
            };

            let toTestEvent;
            if (isCourseSpecific) {
                // Its course specific
                toTestEvent = DataUtils.findTestEventForTermAndMsid(
                    studentTestEvents,
                    selectedTermNumber,
                    courseSpecificBid
                );
            } else {
                // its a subject as per student profile
                toTestEvent = DataUtils.findTestEventForTermAndSubject(
                    studentTestEvents,
                    selectedTermNumber,
                    courseSpecificBid
                );
            }

            if (toTestEvent) {
                let payload = {
                    achievementPercentile: toTestEvent.getIn(['statusNorms', 'studentPercentile'], null),
                    growthPeriods: [],
                };

                if (toTestEvent.get('growthEventYn')) {
                    const fromTermNumbers = getGrowthComparisonFromTermNumbers(selectedTermNumber);
                    fromTermNumbers.forEach((fromTermNumber) => {
                        conditionalPushItem(toTestEvent, fromTermNumber, payload.growthPeriods);
                    });
                }
                return Immutable.fromJS(payload);
            }
        }
        return Immutable.Map();
    } else {
        //There is no previous growth event.  We still need to produce the achievement
        //percentile data, if we have 1 test event to show.

        let achievementData = null;

        let testEvent =
            studentTestEvents && selectedTermNumber !== CommonValues.mostRecentTermNumber
                ? DataUtils.findTestEventForTermAndMsid(studentTestEvents, selectedTermNumber, courseSpecificBid)
                : null;

        if (testEvent) {
            let payload = {
                achievementPercentile: testEvent.getIn(['statusNorms', 'studentPercentile'], null),
                growthPeriods: [],
            };
            achievementData = Immutable.fromJS(payload);
        }

        return achievementData;
    }
}

function precisionRound(number, precision) {
    const factor = Math.pow(10, precision);
    return Math.round(number * factor) / factor;
}

export default {
    getComparisonDropdownPeriods,
    getGrowthComparisonFromTermNumbers,
    getLexileRange,
    getQuantileRange,
    getMeanDescription,
    getValidGrowthTestEvent,
    getValidGrowthTestEventByMsid,
    precisionRound,
};
